import { NotFoundPage } from '@/app/pages/NotFoundPage'
import { organizationRouteTree } from '@/app/routes/organizationRoutes'
import { RoutePendingSkeleton } from '@/app/routes/pendingSkeleton'
import {
	globalAccessDeniedRoute,
	indexRoute,
	loginRoute,
	notFoundRoute,
	rootRoute,
	welcomeRoute,
} from '@/app/routes/rootRoutes'
import { RouteErrorState } from '@/app/routes/routeError'
import { trackPageView } from '@/lib/analytics'
import { setSentryTag } from '@/lib/sentry'
import { PathParamError, createRouter } from '@tanstack/react-router'

/**
 * Composition root for the route tree.
 *
 * Each section assembles its own subtree in a sibling module, so this file shows the shape of the URL
 * space rather than every leaf in it: everything tenanted hangs off the organization route, and only
 * the pages that sit above tenancy are listed here.
 */
const routeTree = rootRoute.addChildren([
	loginRoute,
	welcomeRoute,
	globalAccessDeniedRoute,
	organizationRouteTree,
	indexRoute,
	notFoundRoute,
])

export const router = createRouter({
	routeTree,
	defaultNotFoundComponent: () => <NotFoundPage />,
	// A path segment outside its route's schema is an unknown page, not a crash.
	defaultErrorComponent: props =>
		props.error instanceof PathParamError ? <NotFoundPage /> : <RouteErrorState {...props} />,
	defaultPendingComponent: () => <RoutePendingSkeleton />,
})

// The matched route id names the page with no tenancy or entity id in it; PostHog adds the real URL itself.
// A search only change (filters, groups, the device picker) resolves too and is not a new page.
router.subscribe('onResolved', ({ pathChanged }) => {
	if (pathChanged) {
		const route = router.state.matches.at(-1)?.routeId
		setSentryTag('route', route)
		trackPageView(route)
	}
})

export type AppRouter = typeof router

declare module '@tanstack/react-router' {
	interface Register {
		router: typeof router
	}
}
